export function notYetManaged(element: Element) {
	if (element.classList.contains('plauditQueryLoop--managed')) {
		return false;
	}
	element.classList.toggle('plauditQueryLoop--managed', true);
	return true;
}

interface CommonMemoizerMethods<I, O> {
	has(input: I): boolean;
	clear(input: I): void;
	set(input: I, output: O): void;
}

export interface MemoizedProvider<I, O> extends CommonMemoizerMethods<I, O> {
	(input: I): O;
}
export function memoized<I, O, K = I>(fn: (input: I) => O, keyify: (input: I) => K = v => v as unknown as K): MemoizedProvider<I, O> {
	const cache = new Map<K, O>();
	return Object.assign((input: I) => {
		const key = keyify(input);
		if (cache.has(key)) {
			return cache.get(key) as O;
		}
		const res = fn(input);
		cache.set(key, res);
		return res;
	}, {
		has(input: I) {
			return cache.has(keyify(input));
		},
		clear(input: I) {
			cache.delete(keyify(input));
		},
		set(input: I, output: O) {
			cache.set(keyify(input), output);
		}
	});
}

export interface MemoizedCache<I, O> extends CommonMemoizerMethods<I, O> {
	get(input: I): O;
}
export function memoizedCache<I, O, K = I>(fn: (input: I) => O, keyify: (input: I) => K = v => v as unknown as K): MemoizedCache<I, O> {
	const underlyingCache = memoized(fn, keyify);
	return {
		get: underlyingCache,
		has: underlyingCache.has,
		set: underlyingCache.set,
		clear: underlyingCache.clear
	};
}

export class PromiseCache<I, O, K = I> {
	private readonly underlyingMap = new Map<K, Promise<O>>();
	private readonly underlyingResolvers = new Map<K, ((o: O) => void)|true>();

	public constructor(private readonly keyify: (input: I) => K = v => v as unknown as K) {}

	public get(input: I, timeout?: number): Promise<O> {
		const key = this.keyify(input);
		const existing = this.underlyingMap.get(key);
		if (existing) {
			return this.withPossibleTimeout(existing);
		}
		const res = new Promise<O>(resolve => {
			this.underlyingResolvers.set(key, resolve);
		});
		this.underlyingMap.set(key, res);
		return this.withPossibleTimeout(res, timeout);
	}

	private withPossibleTimeout(promise: Promise<O>, timeout?: number): typeof promise {
		if (timeout) {
			return new Promise((resolve, reject) => {
				const timeoutId = setTimeout(() => reject(new Error('Wait time exceeded')), timeout);
				promise.then(r => {
					clearTimeout(timeoutId);
					resolve(r);
				});
			});
		}
		return promise;
	}

	set(input: I, value: O): void {
		const key = this.keyify(input);
		const possibleResolver = this.underlyingResolvers.get(key);
		if (possibleResolver === true) {
			throw new Error('Cannot update an already-set value in a PromiseCache');
		} else if (possibleResolver) {
			possibleResolver(value);
		} else {
			this.underlyingMap.set(key, Promise.resolve(value));
		}
		this.underlyingResolvers.set(key, true);
	}
}
